// Output-encoding + minimal HTML sanitization for user-authored content that
// must pass through dangerouslySetInnerHTML. Pure string transforms, no DOM
// dependency, so they run identically in Next.js SSR and client render paths.

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

/**
 * Escape every HTML-significant character. Turns any injected markup
 * (e.g. `<img src=x onerror=...>`) into inert, displayable text. Use this on
 * PLAIN TEXT before adding your own trusted markup (see linkify).
 */
export function escapeHtml(text: string): string {
  if (!text) return text;
  return text.replace(/[&<>"']/g, (c) => HTML_ESCAPES[c]);
}

/**
 * Allowlist-style sanitizer for values that are already HTML (e.g. post.body).
 * Strips script/style/embed elements, inline event handlers, and dangerous URI
 * schemes while leaving basic formatting + links intact. For plain text prefer
 * escapeHtml(); this exists for the pre-formatted-HTML render path only.
 */
export function sanitizeHtml(html: string): string {
  if (!html) return html;
  let s = html;
  // Drop dangerous elements together with their content.
  s = s.replace(
    /<\s*(script|style|iframe|object|embed|svg|math|template|noscript|link|meta|base)\b[\s\S]*?<\s*\/\s*\1\s*>/gi,
    '',
  );
  // Drop any remaining open/self-closing forms of those elements.
  s = s.replace(
    /<\s*\/?\s*(script|style|iframe|object|embed|svg|math|template|noscript|link|meta|base)\b[^>]*>/gi,
    '',
  );
  // Strip inline event handlers: on...="...", on...='...', on...=value.
  s = s.replace(/\son[a-z]+\s*=\s*"[^"]*"/gi, '');
  s = s.replace(/\son[a-z]+\s*=\s*'[^']*'/gi, '');
  s = s.replace(/\son[a-z]+\s*=\s*[^\s>]+/gi, '');
  // Neutralize javascript:/vbscript:/data: URIs in href/src.
  s = s.replace(/(href|src)\s*=\s*"\s*(?:javascript|vbscript|data)\s*:[^"]*"/gi, '$1="#"');
  s = s.replace(/(href|src)\s*=\s*'\s*(?:javascript|vbscript|data)\s*:[^']*'/gi, "$1='#'");
  return s;
}
